import { useRef } from "react";
import { FadeUp, Overline } from "@/components/Reveal";

const IMAGES = {
  circuit: "https://images.unsplash.com/photo-1766203551890-2664b619042b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHx3aGl0ZSUyMGNvbXB1dGVyJTIwY2hpcCUyMG1hY3JvfGVufDB8fHx8MTc5MTAzODYwN3ww&ixlib=rb-4.1.0&q=85",
  server: "https://images.unsplash.com/photo-1561233835-f937539b95b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxzZXJ2ZXIlMjByYWNrJTIwbGVkfGVufDB8fHx8MTc5MTA0Njk3NXww&ixlib=rb-4.1.0&q=85",
  consumer: "https://images.unsplash.com/photo-1660945742817-217842d82b55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzaWx2ZXIlMjBsYXB0b3AlMjB3aGl0ZSUyMGJhY2tncm91bmQlMjBubyUyMGxvZ298ZW58MHx8fDE3OTEwNDY5ODd8MA&ixlib=rb-4.1.0&q=85",
};

const CATEGORIES = [
  {
    title: "AI & GPU Compute",
    items: ["Enterprise GPUs", "Professional compute GPUs", "AI accelerators", "GPU servers", "AI workstations", "Multi-GPU systems"],
    span: "lg:col-span-3",
    image: IMAGES.circuit,
  },
  {
    title: "Processing & Components",
    items: ["CPUs", "Processors", "Motherboards", "Power supplies", "Cooling", "Compute components"],
    span: "lg:col-span-3",
  },
  {
    title: "Memory",
    items: ["DDR / DDR5", "ECC memory", "Server memory", "Workstation memory", "High-capacity modules"],
    span: "lg:col-span-2",
  },
  {
    title: "Storage",
    items: ["Enterprise SSD", "NVMe", "HDD", "Data-centre storage", "Portable storage"],
    span: "lg:col-span-2",
  },
  {
    title: "Systems & Infrastructure",
    items: ["Servers", "Workstations", "Networking", "Rack systems", "Compute nodes", "Infrastructure hardware"],
    span: "lg:col-span-2",
    image: IMAGES.server,
  },
  {
    title: "Consumer Technology",
    items: ["Laptops", "Desktops", "Monitors", "Tablets", "Smartphones", "Accessories", "Peripherals", "Selected consumer electronics"],
    span: "lg:col-span-6",
    image: IMAGES.consumer,
    wide: true,
  },
];

const TiltCard = ({ cat, index }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ref.current.style.transform = `perspective(1000px) rotateX(${py * -2}deg) rotateY(${px * 2}deg)`;
  };
  return (
    <FadeUp delay={index * 0.07} className={cat.span}>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => { ref.current.style.transform = "perspective(1000px)"; }}
        data-testid={`category-card-${cat.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="relative h-full bg-white hairline p-9 lg:p-11 overflow-hidden group transition-[border-color,box-shadow] duration-300 hover:border-gold/40 hover:shadow-medium"
      >
        {cat.image && (
          <>
            <img src={cat.image} alt={`${cat.title} — brand-neutral technology hardware`} className="absolute right-0 top-0 w-1/2 h-full object-cover opacity-[0.12] group-hover:opacity-[0.18] transition-opacity duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/30" />
          </>
        )}
        <div className="relative">
          <p className="text-xs font-bold tracking-[0.2em] text-gold mb-5">{String(index + 1).padStart(2, "0")}</p>
          <h3 className="font-heading text-2xl font-medium tracking-tight text-midnight mb-6">{cat.title}</h3>
          <ul className={cat.wide ? "grid sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-xl" : "space-y-2.5"}>
            {cat.items.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-midnight/60">
                <span className="w-3 h-px bg-gold shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </FadeUp>
  );
};

export const Categories = () => (
  <section id="capabilities" className="relative py-16 lg:py-24 bg-white" data-testid="categories-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <FadeUp>
        <Overline>What We Supply</Overline>
        <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-midnight max-w-3xl">
          Advanced compute first. Full-spectrum technology supply.
        </h2>
        <p className="text-midnight/55 text-lg mt-5 max-w-2xl leading-relaxed">
          From enterprise accelerators and data-centre infrastructure to components and selected consumer technology — structured for B2B procurement.
        </p>
      </FadeUp>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5 mt-14">
        {CATEGORIES.map((cat, i) => (
          <TiltCard key={cat.title} cat={cat} index={i} />
        ))}
      </div>
    </div>
  </section>
);
